/**
 * DEFCAT P6 + R2-12/R2-13 — Dependencies tab ★ (inheritance-aware overlay
 * editor).
 *
 * The location list is the SAME L0-rooted tree as the Trees tab (R2-04/R2-13):
 * collapsible component-group roots with their location sub-trees. Select a
 * location node `L`; the Type tree on the right shows, per the design note
 * (`allocation-inheritance.md`):
 *   - OWN includes (solid ticks) — pairs on L (+ type-group → leaf expansion);
 *   - INHERITED includes (faded, still tickable) — include pairs on an ancestor
 *     of L (location walk-up, incl. the L0 link); unticking one writes an
 *     EXCLUSION pair on L; re-ticking removes it;
 *   - EXCLUSIONS (struck) — exclusion pairs on L.
 * "Save dependencies" PUTs the WHOLE overlay, every pair incl. `exclusion`.
 *
 * "Types independent of location" clears the overlay entirely (empty pairs ⇒
 * no gating).
 *
 * L0 nodes (synthetic component-group roots) are EDITABLE exactly like any
 * other location node since taxonomy@1.3.0 (DEFCAT-R2-13): own ticks, legal
 * exclusions, and — being the top of the location walk-up — they gate every
 * descendant location, which then renders those rules as INHERITED. The overlay
 * keys L0 pairs by the SYNTHETIC node id internally; translation to/from the
 * raw component-group UUID the wire carries happens only at load/save (see
 * `treeBuild.ts` `rawL0ToNodeId` / `nodeIdToRawL0`).
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import type {
  CatalogueValidityPair,
  GetDefectCatalogueV2Response,
} from "@aeriosiq/types";
import { TreePanel } from "./TreePanel.js";
import {
  buildAxisTree,
  buildLocationTreeWithL0,
  isL0NodeId,
  nodeIdToRawL0,
  rawL0ToNodeId,
  referencedL0GroupIds,
} from "./treeBuild.js";
import { allocationSetsForLocation } from "./allocationResolve.js";
import { useComponentGroupNameResolver } from "./componentGroups.js";
import { usePutValidityMutation } from "./taxonomyQueries.js";
import { locText } from "./locText.js";

export interface DependenciesTabProps {
  catalogueId: string;
  versionId: string;
  structure: GetDefectCatalogueV2Response;
  editable: boolean;
}

/** Stable pair key (location + type) ignoring the exclusion flag. */
function pairKey(locationEntryId: string, typeEntryId: string): string {
  return `${locationEntryId} ${typeEntryId}`;
}

export function DependenciesTab({
  catalogueId,
  versionId,
  structure,
  editable,
}: DependenciesTabProps): ReactElement {
  const groupName = useComponentGroupNameResolver();
  const locationTree = useMemo(
    () => buildLocationTreeWithL0(structure.entries, groupName),
    [structure.entries, groupName],
  );
  const typeTree = useMemo(
    () => buildAxisTree(structure.entries, "type"),
    [structure.entries],
  );

  // Raw component-group UUIDs the version references — the L0 allocation
  // targets the wire recognises. Used to translate wire pairs ↔ synthetic ids.
  const l0Ids = useMemo(
    () => referencedL0GroupIds(structure.entries),
    [structure.entries],
  );

  // Local editable overlay as a keyed pair map (preserves exclusion flags).
  // L0 pairs are normalised to the SYNTHETIC node id on load so the overlay,
  // resolve, and tree all speak the same id; save translates back to raw.
  const [overlay, setOverlay] = useState<Map<string, CatalogueValidityPair>>(
    () => initOverlay(structure.validity, l0Ids),
  );
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    null,
  );
  const put = usePutValidityMutation(catalogueId, versionId);

  useEffect(() => {
    setOverlay(initOverlay(structure.validity, l0Ids));
  }, [structure.validity, l0Ids]);

  const pairs = useMemo(() => [...overlay.values()], [overlay]);
  const independent = overlay.size === 0;

  const selectedL0 =
    selectedLocationId !== null && isL0NodeId(selectedLocationId);

  // Inheritance-aware sets for the selected location (own/inherited/excluded).
  const sets = useMemo(
    () =>
      allocationSetsForLocation(pairs, structure.entries, selectedLocationId),
    [pairs, structure.entries, selectedLocationId],
  );

  /**
   * Toggle a type for the selected location. The semantics depend on whether
   * the tick is currently OWN, INHERITED, or EXCLUDED:
   *  - inherited + untick → add an EXCLUSION pair on L;
   *  - excluded + re-tick → remove the exclusion (back to inherited);
   *  - otherwise add/remove an OWN include pair on L.
   */
  function toggleType(typeEntryId: string, checked: boolean): void {
    if (selectedLocationId === null) return;
    setOverlay((prev) => {
      const next = new Map(prev);
      const k = pairKey(selectedLocationId, typeEntryId);
      const existing = next.get(k);
      const isInherited = sets.inherited.has(typeEntryId);
      const isExcluded = sets.excluded.has(typeEntryId);

      if (!checked && isInherited && existing === undefined) {
        // Untick an inherited tick → record a node-local exclusion.
        next.set(k, {
          locationEntryId: selectedLocationId,
          typeEntryId,
          exclusion: true,
        });
      } else if (checked && isExcluded) {
        // Re-tick an excluded type → drop the exclusion (re-inherit).
        next.delete(k);
      } else if (checked) {
        // Add an OWN include pair.
        next.set(k, { locationEntryId: selectedLocationId, typeEntryId });
      } else {
        // Untick an OWN include → remove the pair.
        next.delete(k);
      }
      return next;
    });
  }

  function handleIndependentToggle(makeIndependent: boolean): void {
    if (makeIndependent) setOverlay(new Map());
  }

  function handleSave(): void {
    // Translate the internal synthetic L0 ids back to the raw component-group
    // UUIDs the wire carries; real location entryIds pass through unchanged.
    put.mutate({ pairs: pairs.map(toWirePair) });
  }

  const ownCount = sets.own.size;
  const inheritedCount = sets.inherited.size;
  const excludedCount = sets.excluded.size;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        minHeight: 0,
        height: "100%",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ flexShrink: 0, flexWrap: "wrap", rowGap: 1 }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={independent}
              disabled={!editable}
              onChange={(e) => {
                handleIndependentToggle(e.target.checked);
              }}
            />
          }
          label="Types independent of location (no gating)"
        />
        <Stack direction="row" spacing={1} alignItems="center">
          {put.isError ? (
            <Typography color="error" variant="body2">
              Save failed
            </Typography>
          ) : null}
          {put.isSuccess ? (
            <Typography color="success.main" variant="body2">
              Saved
            </Typography>
          ) : null}
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!editable || put.isPending}
          >
            Save dependencies
          </Button>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ flexShrink: 0 }}>
        Select a location, then tick the type nodes valid there. Group
        allocations are inherited downward (faded ticks); unticking an inherited
        type records an exclusion for this location only. Excluded types render
        struck.
      </Alert>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          minHeight: 0,
          flex: 1,
        }}
      >
        <Paper
          variant="outlined"
          sx={{ p: 1.5, minHeight: 0, display: "flex" }}
        >
          <TreePanel
            title="Location tree (select one)"
            nodes={locationTree}
            selectedId={selectedLocationId}
            onSelect={setSelectedLocationId}
          />
        </Paper>
        <Paper
          variant="outlined"
          sx={{ p: 1.5, minHeight: 0, display: "flex" }}
        >
          {selectedLocationId === null ? (
            <Typography variant="body2" color="text.secondary">
              Select a location node to edit its valid types.
            </Typography>
          ) : (
            <Stack sx={{ width: "100%", minHeight: 0 }}>
              {selectedL0 ? (
                <Alert severity="info" sx={{ mb: 1, flexShrink: 0 }}>
                  Allocating types here gates EVERY location below this
                  component group — descendants inherit them (faded) and may add
                  their own includes or exclusions.
                </Alert>
              ) : null}
              <TreePanel
                title={`Valid types — own ${String(ownCount)} · inherited ${String(
                  inheritedCount,
                )} · excluded ${String(excludedCount)}`}
                nodes={typeTree}
                selectedId={null}
                onSelect={() => {
                  /* selection not used in checkable mode */
                }}
                checkable
                checkedIds={sets.own}
                inheritedIds={sets.inherited}
                excludedIds={sets.excluded}
                onToggleCheck={editable ? toggleType : undefined}
              />
            </Stack>
          )}
        </Paper>
      </Box>
      {!selectedL0 && sets.inheritedSource.size > 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
          Faded ticks are inherited from{" "}
          {summariseInheritedSources(sets.inheritedSource, structure.entries)}.
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * Seed the overlay map from the read pairs, keyed by (location,type). A wire
 * pair whose `locationEntryId` is a raw L0 component-group UUID is normalised
 * to the synthetic node id so resolve + the location tree agree on one id.
 */
function initOverlay(
  validity: CatalogueValidityPair[] | null | undefined,
  l0Ids: ReadonlySet<string>,
): Map<string, CatalogueValidityPair> {
  const m = new Map<string, CatalogueValidityPair>();
  for (const p of validity ?? []) {
    const locId = rawL0ToNodeId(p.locationEntryId, l0Ids);
    m.set(pairKey(locId, p.typeEntryId), {
      locationEntryId: locId,
      typeEntryId: p.typeEntryId,
      ...(p.exclusion === true ? { exclusion: true } : {}),
    });
  }
  return m;
}

/**
 * Translate one overlay pair to its wire shape: a synthetic L0 node id becomes
 * the raw component-group UUID; real location entryIds pass through unchanged.
 */
function toWirePair(p: CatalogueValidityPair): CatalogueValidityPair {
  return {
    locationEntryId: nodeIdToRawL0(p.locationEntryId),
    typeEntryId: p.typeEntryId,
    ...(p.exclusion === true ? { exclusion: true } : {}),
  };
}

/** Human-readable list of the ancestor location names supplying inherited ticks. */
function summariseInheritedSources(
  inheritedSource: ReadonlyMap<string, string>,
  entries: GetDefectCatalogueV2Response["entries"],
): string {
  const names = new Set<string>();
  for (const locId of inheritedSource.values()) {
    if (isL0NodeId(locId)) {
      names.add("the component group");
      continue;
    }
    const e = entries.find((x) => x.entryId === locId);
    if (e) names.add(locText(e.name));
  }
  return [...names].join(", ");
}
